import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Users,
  Clock,
  MapPin,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { useSport } from '../context/SportContext';

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const PlannerDayPage = () => {
  const { date } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t } = useLanguage();
  const [sessions, setSessions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [teamFilter, setTeamFilter] = useState('all');

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (teamFilter !== 'all') params.append('team_id', teamFilter);
      const response = await api.get(`/sessions/by-date/${date}?${params.toString()}`);
      setSessions(response.data);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [api, date, teamFilter]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  }, [api]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handlePrevDay = () => {
    const prev = new Date(date);
    prev.setDate(prev.getDate() - 1);
    navigate(`/staff/planner/day/${prev.toISOString().split('T')[0]}`);
  };

  const handleNextDay = () => {
    const next = new Date(date);
    next.setDate(next.getDate() + 1);
    navigate(`/staff/planner/day/${next.toISOString().split('T')[0]}`);
  };

  const teamSessions = sessions.filter(s => s.program_type === 'TEAM');
  const rtpSessions = sessions.filter(s => s.program_type === 'RTP');
  
  const totalAssigned = sessions.reduce((acc, s) => acc + (s.assignment_count || 0), 0);
  const totalCompleted = sessions.reduce((acc, s) => acc + (s.completed_count || 0), 0);
  const totalMissed = sessions.reduce((acc, s) => acc + (s.missed_count || 0), 0);

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          onClick={() => navigate('/staff/planner')}
          variant="ghost"
          size="icon"
          className="text-zinc-400 hover:text-zinc-100"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-zinc-900 border border-zinc-800 rounded-sm">
              <Button
                onClick={handlePrevDay}
                variant="ghost"
                size="icon"
                className="text-zinc-400 hover:text-zinc-100 h-8 w-8"
                data-testid="prev-day"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-3 text-sm font-medium text-zinc-100">
                <Calendar className="w-4 h-4 inline mr-2" />
                {formatDate(date)}
              </span>
              <Button
                onClick={handleNextDay}
                variant="ghost"
                size="icon"
                className="text-zinc-400 hover:text-zinc-100 h-8 w-8"
                data-testid="next-day"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <Select value={teamFilter} onValueChange={setTeamFilter}>
          <SelectTrigger className="w-[180px] bg-zinc-950 border-zinc-800 rounded-sm" data-testid="team-filter">
            <SelectValue placeholder="All Teams" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">{t('all')} {t('teams')}</SelectItem>
            {teams.map((team) => (
              <SelectItem key={team.id} value={team.id}>{team.code}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 text-center">
          <div className="text-3xl font-bold text-zinc-100">{sessions.length}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Sessions</div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 text-center">
          <div className="text-3xl font-bold text-amber-500">{totalAssigned}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Assigned</div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 text-center">
          <div className="text-3xl font-bold text-emerald-500">{totalCompleted}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Completed</div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 text-center">
          <div className="text-3xl font-bold text-rose-500">{totalMissed}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Missed</div>
        </div>
      </div>

      {/* Sessions by Program Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team Sessions */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm">
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="font-semibold text-zinc-100">Team Program ({teamSessions.length})</h2>
            <span className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded">TEAM</span>
          </div>
          <div className="p-4 space-y-3">
            {isLoading ? (
              <div className="text-center py-8 text-zinc-500">{t('loading')}</div>
            ) : teamSessions.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">No team sessions</div>
            ) : (
              teamSessions.map((session) => (
                <SessionCard key={session.id} session={session} />
              ))
            )}
          </div>
        </div>

        {/* RTP Sessions */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm">
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="font-semibold text-zinc-100">RTP Program ({rtpSessions.length})</h2>
            <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded">RTP</span>
          </div>
          <div className="p-4 space-y-3">
            {isLoading ? (
              <div className="text-center py-8 text-zinc-500">{t('loading')}</div>
            ) : rtpSessions.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">No RTP sessions</div>
            ) : (
              rtpSessions.map((session) => (
                <SessionCard key={session.id} session={session} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="flex gap-4">
        <Link
          to={`/staff/planner/snapshot/${date}`}
          className="text-sm text-zinc-500 hover:text-amber-500 transition-colors"
        >
          View Day Snapshot →
        </Link>
      </div>
    </div>
  );
};

const SessionCard = ({ session }) => {
  const { getSessionTypeMeta } = useSport();
  const blocks = session.content_json?.blocks || [];
  return (
    <Link
      to={`/staff/planner/session/${session.id}`}
      className={`block p-4 rounded-sm border transition-colors hover:bg-zinc-800/30 ${getSessionTypeMeta(session.type).color}`}
      data-testid={`session-card-${session.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{session.title}</div>
          <div className="text-xs opacity-70 mt-1 space-y-1">
            <div>{session.team_code} • {session.type}</div>
            {session.location && (
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {session.location}
              </div>
            )}
            {session.duration_planned_min && (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {session.duration_planned_min} min
              </div>
            )}
          </div>
          {blocks.length > 0 && (
            <div className="mt-2 pt-2 border-t border-current/10 space-y-0.5">
              {blocks.slice(0, 4).map((b, i) => (
                <div key={i} className="text-[11px] opacity-70 flex items-center gap-1">
                  <div className="w-1 h-1 rounded-full bg-current opacity-50" />
                  <span>{b.name || b.type}</span>
                  {b.sets && <span className="opacity-60">— {b.sets}x{b.reps || b.duration}</span>}
                  {b.intensity && <span className="opacity-80 ml-0.5">@{b.intensity}</span>}
                </div>
              ))}
              {blocks.length > 4 && (
                <div className="text-[10px] opacity-50">+{blocks.length - 4} more</div>
              )}
            </div>
          )}
          {!blocks.length && session.description && (
            <p className="mt-1 text-[11px] opacity-50 line-clamp-2">{session.description}</p>
          )}
        </div>
        <div className="text-right ml-3 shrink-0">
          <div className="flex items-center gap-2 text-xs">
            <Users className="w-3 h-3" />
            <span>{session.assignment_count || 0}</span>
          </div>
          {session.completed_count > 0 && (
            <div className="flex items-center gap-1 text-emerald-400 text-xs mt-1">
              <CheckCircle className="w-3 h-3" /> {session.completed_count}
            </div>
          )}
          {session.missed_count > 0 && (
            <div className="flex items-center gap-1 text-rose-400 text-xs mt-1">
              <XCircle className="w-3 h-3" /> {session.missed_count}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default PlannerDayPage;
